/**
 * formStateAdapter — converts between the schema-driven form state and the
 * Supabase intake_forms / intake_field_data storage format.
 *
 * The schema-driven form stores values keyed by canonical_name
 * (e.g. "applicant.passport.family_name"). These are stored one row per field
 * in intake_field_data with the canonical_name as field_key. This mirrors
 * exactly how the v1 IntakeForm works, so the same DB rows can be read by both
 * flows during the parallel-run period.
 *
 * FormState shape: Record<string, unknown>
 *   Keys are canonical_names (dot-notation), values are field values.
 *   Repeater fields use arrays at the top-level canonical_name.
 *
 *   Example:
 *   {
 *     "applicant.passport.family_name": "Smith",
 *     "applicant.passport.given_names": "Jane",
 *     "applicant.children": [{ "family_name": "Smith", "dob": "2015-04-12" }]
 *   }
 */

import type { Json } from '../database.types';

/** The flat form state that react-hook-form holds internally. */
export type FormState = Record<string, unknown>;

/** One row in the intake_field_data table. */
export interface FieldRow {
  intake_form_id: string;
  field_key: string;
  field_value: Json;
}

/**
 * Convert the react-hook-form values (flat canonical_name keys) into DB rows.
 *
 * Empty / undefined values are omitted. Arrays and objects are stored as JSON.
 */
export function formStateToRows(formId: string, state: FormState): FieldRow[] {
  const rows: FieldRow[] = [];

  for (const [key, value] of Object.entries(state)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value === '') continue;

    rows.push({
      intake_form_id: formId,
      field_key: key,
      field_value: value as Json,
    });
  }

  return rows;
}

/**
 * Convert DB rows back into the flat FormState used by react-hook-form.
 * The canonical_name is used directly as the key.
 */
export function rowsToFormState(
  rows: Array<{ field_key: string; field_value: unknown }>,
): FormState {
  const state: FormState = {};
  for (const row of rows) {
    if (row.field_key) {
      state[row.field_key] = row.field_value;
    }
  }
  return state;
}

/**
 * Expand a flat FormState (mixed top-level blob rows + dotted seed rows) into
 * the nested object react-hook-form needs at reset().
 *
 * WHY: RHF resolves a field name like "applicant.postal_address.line1" as a
 * NESTED path, with a literal flat-key fallback only for the exact string.
 * Handing reset() the raw flat row map therefore renders scalar dotted rows
 * (via the fallback) but silently drops any OBJECT stored at a dotted key —
 * address seeds like "applicant.postal_address" never reached their
 * ".line1" inputs, while the PDF path (which expands) showed them fine.
 *
 * PRECEDENCE: top-level blob rows first (autosave output — the freshest,
 * carries every client/staff edit), then dotted rows fill ONLY leaves the
 * blobs left empty. A seed row can therefore never override a later edit,
 * and a backfilled seed surfaces exactly when nobody has touched the field.
 */
export function expandFormState(state: FormState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const entries = Object.entries(state);

  for (const [key, value] of entries) {
    if (!key.includes('.')) out[key] = value;
  }

  for (const [key, value] of entries) {
    if (!key.includes('.')) continue;
    const parts = key.split('.');
    let cur = out;
    let blocked = false;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      const next = cur[k];
      if (next === undefined || next === null) {
        cur[k] = {};
      } else if (typeof next !== 'object' || Array.isArray(next)) {
        // A non-object already occupies this path segment — do not descend.
        blocked = true;
        break;
      }
      cur = cur[k] as Record<string, unknown>;
    }
    if (blocked) continue;
    const leaf = parts[parts.length - 1];
    const existing = cur[leaf];
    if (existing === undefined || existing === null || existing === '') {
      cur[leaf] = value;
    }
  }

  return out;
}

/**
 * Build the nested, canonical-path-addressed object the PDF generator expects,
 * from the flat schema-driven (v2) FormState.
 *
 * V2 stores values keyed by canonical_name (e.g. "applicant.passport.family_name"),
 * with repeaters as arrays at the top-level canonical_name. The PDF renderer reads
 * SCHEMA canonical paths (nested), so we expand the dot-notation keys into a nested
 * object. Repeater arrays / nested objects are set whole at their leaf path.
 *
 * No v1 translation is needed — v2's keys ARE the schema paths. This replaces the
 * v1FormDataMapper for any form filled directly in the schema-driven intake.
 */
export function buildSchemaFormData(state: FormState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(state)) {
    if (value === undefined || value === null || value === '') continue;
    const parts = key.split('.');
    let cur = out;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      if (typeof cur[k] !== 'object' || cur[k] === null) cur[k] = {};
      cur = cur[k] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = value;
  }
  return out;
}

/**
 * Read a dot-path value from a NESTED object (e.g. the output of
 * expandFormState). Walks plain objects only — arrays and other non-object
 * values encountered along the path abort the walk and return undefined.
 */
export function readNestedPath(obj: unknown, path: string): unknown {
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur === null || typeof cur !== 'object' || Array.isArray(cur)) return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/**
 * Merge a context object (e.g. cross-form data loaded from another intake)
 * into the current state without overwriting existing values.
 *
 * Used when a sponsor's form pre-loads relationship facts from the applicant's
 * record, or when an applicant form pre-loads sponsor identity.
 */
export function mergeReadOnlyContext(
  state: FormState,
  context: FormState,
): FormState {
  const merged = { ...state };
  for (const [key, value] of Object.entries(context)) {
    if (!(key in merged) || merged[key] === undefined || merged[key] === null || merged[key] === '') {
      merged[key] = value;
    }
  }
  return merged;
}
